// StatusIndicator.jsx
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { statusIndicator } from '@digital_monorepo/styling';
import { updateProfessionalStatus } from '@/services/schedulingService';

const STATUSES = ['green', 'yellow', 'red', 'blank'];
const MENU_HEIGHT_ESTIMATE = 4 * 34 + 8;

const StatusIndicator = ({ professionalID, status, onChange }) => {
  const { t } = useTranslation();
  const [localStatus, setLocalStatus] = useState(status);
  const [isSaving, setIsSaving] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const triggerRef = useRef(null);

  // Keeps this instance in sync when the same professional's status is
  // changed elsewhere on the page (e.g. a different week's table row).
  useEffect(() => setLocalStatus(status), [status]);

  useEffect(() => {
    if (!menuPos) return undefined;
    const close = () => setMenuPos(null);
    const handleKey = (e) => { if (e.key === 'Escape') close(); };
    // Fixed-position menu, so a plain scroll/resize listener (capture: true
    // catches the scrollable table body too) is enough to keep it from
    // drifting away from its trigger.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuPos]);

  const labelFor = (option) => t(`STATUS_INDICATOR.${option.toUpperCase()}`);

  const openMenu = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    const openUpward = window.innerHeight - rect.bottom < MENU_HEIGHT_ESTIMATE + 12;
    setMenuPos({
      left: rect.left,
      top: openUpward ? undefined : rect.bottom + 4,
      bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
    });
  };

  const handleSelect = async (newStatus) => {
    setMenuPos(null);
    if (newStatus === localStatus || isSaving) return;
    const previous = localStatus;
    setLocalStatus(newStatus);
    setIsSaving(true);
    try {
      await updateProfessionalStatus(professionalID, newStatus);
      onChange?.(newStatus);
    } catch (error) {
      console.error('Error updating professional status:', error);
      setLocalStatus(previous);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={statusIndicator.wrapper}>
      <button
        type="button"
        ref={triggerRef}
        className={`${statusIndicator.pill} ${statusIndicator[localStatus]}`}
        onClick={() => (menuPos ? setMenuPos(null) : openMenu())}
        disabled={isSaving}
        title={t('STATUS_INDICATOR.EDIT_HINT')}
        aria-haspopup="listbox"
        aria-expanded={!!menuPos}
      >
        <span className={`${statusIndicator.dot} ${statusIndicator[localStatus]}`} />
        <span className={statusIndicator.label}>{labelFor(localStatus)}</span>
      </button>

      {menuPos && (
        <>
          <div className={statusIndicator.overlay} onClick={() => setMenuPos(null)} />
          <div className={statusIndicator.menu} role="listbox" style={menuPos}>
            {STATUSES.map((option) => (
              <button
                type="button"
                key={option}
                role="option"
                aria-selected={option === localStatus}
                className={`${statusIndicator.option} ${option === localStatus ? statusIndicator.optionSelected : ''}`}
                onClick={() => handleSelect(option)}
              >
                <span className={`${statusIndicator.dot} ${statusIndicator[option]}`} />
                <span>{labelFor(option)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default StatusIndicator;
